import type { AiFacilitatorResult } from "../../types";
import { HAUFFE_LOGO } from "../brand";

interface AiSummaryPdfInput {
  workspaceName: string;
  createdAt: string;
  result: AiFacilitatorResult;
  language?: "de" | "en";
}

interface PdfLogoImage {
  bytes: Uint8Array;
  width: number;
  height: number;
}

interface PdfBrandImages {
  logo?: PdfLogoImage;
}

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGIN_X = 54;
const MARGIN_BOTTOM = 62;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_X * 2;
const HEADER_HEIGHT_FIRST = 112;
const HEADER_HEIGHT_CONTINUED = 76;
const LINE_HEIGHT = 14;

export function normalizePdfText(value: string): string {
  return value
    .replace(/[–—]/g, "-")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, " ")
    .normalize("NFC")
    .trim();
}

function normalizePdfDedupeKey(value: string): string {
  return normalizePdfText(value)
    .toLowerCase()
    .replace(/[.,;:!?()[\]"']/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function dedupePdfTextItems(items: string[]): string[] {
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const item of items) {
    const key = normalizePdfDedupeKey(item);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    deduped.push(item);
  }
  return deduped;
}

export function encodePdfText(value: string): string {
  const normalized = normalizePdfText(value);
  let encoded = "";
  for (const char of normalized) {
    const codePoint = char.codePointAt(0) ?? 0x20;
    const byte = codePoint <= 0xff ? codePoint : 0x20;
    if (byte === 0x28 || byte === 0x29 || byte === 0x5c) {
      encoded += `\\${String.fromCharCode(byte)}`;
    } else if (byte < 0x20 || byte > 0x7e) {
      encoded += `\\${byte.toString(8).padStart(3, "0")}`;
    } else {
      encoded += String.fromCharCode(byte);
    }
  }
  return `(${encoded})`;
}

function slugifyFilename(value: string): string {
  const slug = value
    .replace(/[äÄ]/g, "ae")
    .replace(/[öÖ]/g, "oe")
    .replace(/[üÜ]/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^\x20-\x7e\n]/g, " ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "workspace";
}

function wrapText(text: string, maxChars: number): string[] {
  const words = normalizePdfText(text).split(" ").filter(Boolean);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines.length ? lines : [""];
}

function workspaceRecommendation(item: string, language: "de" | "en"): string {
  const normalized = item.toLowerCase();
  if (normalized.includes("review the suggested customer value criteria") || normalized.includes("prüfen sie die vorgeschlagenen customer-value-kriterien")) {
    return item;
  }
  if (normalized.includes("reviewe")) {
    return language === "en"
      ? "Review and finalize criteria and weightings in the workspace."
      : "Prüfen und finalisieren Sie die Kriterien und Gewichtungen als Company Owner im Workspace.";
  }
  if (normalized.includes("scoring") || normalized.includes("pilot") || normalized.includes("pilotiere")) {
    return language === "en"
      ? "Start by evaluating a small selection of existing customers in the digital Customer Value Model."
      : "Bewerten Sie zuerst eine kleine Auswahl bestehender Kunden im digitalen Customer Value Modell.";
  }
  if (normalized.includes("workshop") || normalized.includes("moderiert") || normalized.includes("termin") || normalized.includes("addon")) {
    if (normalized.includes("friction") || normalized.includes("reibung")) {
      return language === "en"
        ? "Use visible friction points as checkpoints for the Customer Value Calculation."
        : "Nutzen Sie sichtbare Reibungspunkte als Prüfpunkte für die Customer Value Calculation.";
    }
    return language === "en"
      ? "Review the suggested Customer Value Criteria in the workspace and finalize weightings and scoring logic."
      : "Prüfen Sie die vorgeschlagenen Customer-Value-Kriterien im Workspace und finalisieren Sie Gewichtungen und Bewertungslogik.";
  }
  if (normalized.includes("crm")) {
    return language === "en"
      ? "Adopt suggested CRM fields as the data basis for later evaluation and segmentation."
      : "Übernehmen Sie vorgeschlagene CRM-Felder als Datenbasis für spätere Auswertung und Segmentierung.";
  }
  return item;
}

interface PdfLine {
  text: string;
  size: number;
  bold?: boolean;
  color?: "gold" | "muted" | "text";
  gapBefore?: number;
  kind?: "title" | "section" | "subheading" | "body";
}

function addWrapped(lines: PdfLine[], text: string, options: Partial<Omit<PdfLine, "text">> & { maxChars?: number } = {}) {
  const size = options.size ?? 10;
  const estimatedMaxChars = Math.max(32, Math.floor(CONTENT_WIDTH / (size * 0.62)));
  for (const line of wrapText(text, options.maxChars ?? estimatedMaxChars)) {
    lines.push({ text: line, size, bold: options.bold, color: options.color, gapBefore: options.gapBefore });
    options.gapBefore = undefined;
  }
}

function criterionTypeLabel(type: string, language: "de" | "en"): string {
  const de: Record<string, string> = { positive: "positiv", negative: "negativ", neutral: "neutral" };
  const en: Record<string, string> = { positive: "positive", negative: "negative", neutral: "neutral" };
  return (language === "en" ? en : de)[type] ?? type;
}

export function buildReportLines(input: AiSummaryPdfInput): PdfLine[] {
  const language = input.language ?? "de";
  const result = input.result;
  const createdAt = new Date(input.createdAt).toLocaleDateString(language === "en" ? "en-US" : "de-DE");
  const lines: PdfLine[] = [
    { text: language === "en" ? "HAUFFE OS Evaluation" : "HAUFFE OS Auswertung", size: 26, bold: true, kind: "title" },
    { text: input.workspaceName, size: 14, bold: true, color: "muted", gapBefore: 8 },
    { text: `${language === "en" ? "Evaluation date" : "Datum der Auswertung"}: ${createdAt}`, size: 10, color: "muted", gapBefore: 4 }
  ];

  addWrapped(
    lines,
    language === "en"
      ? "This evaluation is based on the submitted department perspectives and the HAUFFE OS Customer Value methodology."
      : "Diese Auswertung basiert auf den eingereichten Abteilungsperspektiven und der HAUFFE OS Customer Value Methodik.",
    { size: 10, gapBefore: 20 }
  );

  lines.push({ text: language === "en" ? "Executive Summary" : "Kurze Zusammenfassung", size: 16, bold: true, color: "gold", gapBefore: 34, kind: "section" });
  addWrapped(lines, result.workspace_summary, { size: 10 });

  if (result.decision_friction_signals.length) {
    lines.push({ text: language === "en" ? "Key Friction Points" : "Wichtigste Reibungspunkte", size: 12, bold: true, gapBefore: 18, kind: "subheading" });
    for (const signal of result.decision_friction_signals.slice(0, 5)) {
      addWrapped(lines, `- ${signal.signal}: ${signal.why_it_matters}`, { size: 10, maxChars: 72 });
    }
  }

  lines.push({ text: language === "en" ? "Shared Customer Value Criteria" : "Gemeinsame Customer-Value-Kriterien", size: 16, bold: true, color: "gold", gapBefore: 34, kind: "section" });
  for (const criterion of result.customer_value_criteria_suggestions) {
    addWrapped(lines, `${criterion.name} (${criterionTypeLabel(criterion.type, language)})`, { size: 11, bold: true, gapBefore: 8 });
    addWrapped(lines, criterion.reason, { size: 10 });
    if (criterion.source_departments.length) {
      addWrapped(lines, `${language === "en" ? "Relevant perspectives" : "Relevante Perspektiven"}: ${criterion.source_departments.join(", ")}`, { size: 9, color: "muted" });
    }
  }

  lines.push({ text: language === "en" ? "Decision Friction and Risks" : "Entscheidungsreibung und Risiken", size: 16, bold: true, color: "gold", gapBefore: 34, kind: "section" });
  for (const signal of result.decision_friction_signals) {
    addWrapped(lines, `- ${signal.signal}`, { size: 10, bold: true });
    addWrapped(lines, signal.why_it_matters, { size: 10 });
  }

  lines.push({ text: language === "en" ? "Missing Criteria and Open Clarification Questions" : "Fehlende Kriterien und offene Klärungsfragen", size: 16, bold: true, color: "gold", gapBefore: 34, kind: "section" });
  for (const item of result.missing_criteria ?? []) addWrapped(lines, `- ${item}`, { size: 10 });
  for (const item of result.open_clarification_questions) addWrapped(lines, `- ${item}`, { size: 10 });

  const summary = result.observations_vs_derivations_vs_recommendations_summary;
  if (summary) {
    lines.push({ text: language === "en" ? "Observation, Derivation and Recommendation" : "Beobachtung, Ableitung und Empfehlung", size: 16, bold: true, color: "gold", gapBefore: 34, kind: "section" });
    lines.push({ text: language === "en" ? "Observations" : "Beobachtungen", size: 12, bold: true, gapBefore: 14, kind: "subheading" });
    for (const item of summary.observations) addWrapped(lines, `- ${item}`, { size: 10 });
    lines.push({ text: language === "en" ? "Derivations" : "Ableitungen", size: 12, bold: true, gapBefore: 14, kind: "subheading" });
    for (const item of summary.derivations) addWrapped(lines, `- ${item}`, { size: 10 });
    lines.push({ text: language === "en" ? "Recommendations" : "Empfehlungen", size: 12, bold: true, gapBefore: 14, kind: "subheading" });
    for (const item of dedupePdfTextItems(summary.recommendations.map((recommendation) => workspaceRecommendation(recommendation, language)))) {
      addWrapped(lines, `- ${item}`, { size: 10 });
    }
  }

  lines.push({ text: language === "en" ? "Next step: Identify Customer Value Criteria" : "Nächster Schritt: Customer-Value-Kriterien ermitteln", size: 16, bold: true, color: "gold", gapBefore: 34, kind: "section" });
  addWrapped(
    lines,
    language === "en"
      ? "The HAUFFE OS Evaluation has made shared patterns, friction points and open clarification questions visible. In the next step, departments additionally capture what matters to them in a valuable customer. These inputs are clustered into Customer Value Criteria and then weighted."
      : "Die HAUFFE OS Auswertung hat gemeinsame Muster, Reibungspunkte und offene Klärungsfragen sichtbar gemacht. Im nächsten Schritt erfassen die Abteilungen zusätzlich, was ihnen an einem wertvollen Kunden wichtig ist. Daraus werden Customer-Value-Kriterien geclustert und anschließend gewichtet.",
    { size: 10 }
  );

  return lines;
}

function colorCommand(color: PdfLine["color"]): string {
  if (color === "gold") return "0.72 0.49 0.12 rg";
  if (color === "muted") return "0.36 0.38 0.42 rg";
  return "0.08 0.09 0.11 rg";
}

function pageStartY(pageIndex: number): number {
  return PAGE_HEIGHT - (pageIndex === 0 ? HEADER_HEIGHT_FIRST + 34 : HEADER_HEIGHT_CONTINUED + 30);
}

function buildPageContent(lines: PdfLine[], pageIndex: number, pageCount: number, images: { logo?: boolean }, language: "de" | "en"): string {
  const headerHeight = pageIndex === 0 ? HEADER_HEIGHT_FIRST : HEADER_HEIGHT_CONTINUED;
  const commands = [
    "0.98 0.97 0.94 rg",
    `0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT} re f`,
    "0.025 0.025 0.025 rg",
    `0 ${PAGE_HEIGHT - headerHeight} ${PAGE_WIDTH} ${headerHeight} re f`,
    "0.72 0.49 0.12 rg",
    `0 ${PAGE_HEIGHT - headerHeight} ${PAGE_WIDTH} 3 re f`
  ];
  if (images.logo) {
    commands.push("q");
    const logoWidth = pageIndex === 0 ? 150 : 108;
    const logoHeight = pageIndex === 0 ? 53 : 38;
    commands.push(`${logoWidth} 0 0 ${logoHeight} ${MARGIN_X} ${PAGE_HEIGHT - headerHeight + (headerHeight - logoHeight) / 2} cm`);
    commands.push("/Logo Do");
    commands.push("Q");
  } else {
    commands.push("BT");
    commands.push("0.98 0.97 0.94 rg");
    commands.push("/F2 16 Tf");
    commands.push(`${MARGIN_X} ${PAGE_HEIGHT - headerHeight + 42} Td`);
    commands.push(`${encodePdfText("HAUFFE OS")} Tj`);
    commands.push("ET");
  }
  commands.push("BT");
  commands.push("0.72 0.49 0.12 rg");
  commands.push("/F1 8 Tf");
  commands.push(`${PAGE_WIDTH - MARGIN_X - 152} ${PAGE_HEIGHT - headerHeight + 32} Td`);
  commands.push(`${encodePdfText("Customer Value Workspace")} Tj`);
  commands.push("ET");

  let y = pageStartY(pageIndex);
  if (pageIndex > 0) {
    commands.push("BT");
    commands.push("0.36 0.38 0.42 rg");
    commands.push("/F1 8 Tf");
    commands.push(`${MARGIN_X} ${y + 18} Td`);
    commands.push(`${encodePdfText(language === "en" ? "HAUFFE OS Evaluation - continued" : "HAUFFE OS Auswertung - Fortsetzung")} Tj`);
    commands.push("ET");
  }
  for (const line of lines) {
    y -= line.gapBefore ?? 0;
    const textX = MARGIN_X;
    if (line.kind === "section") {
      commands.push("0.72 0.49 0.12 rg");
      commands.push(`${MARGIN_X} ${(y - 9).toFixed(2)} 24 1.8 re f`);
    }
    commands.push("BT");
    commands.push(colorCommand(line.color));
    commands.push(`/${line.bold ? "F2" : "F1"} ${line.size} Tf`);
    commands.push(`${textX} ${y.toFixed(2)} Td`);
    commands.push(`${encodePdfText(line.text)} Tj`);
    commands.push("ET");
    y -= Math.max(LINE_HEIGHT, line.size + (line.kind === "section" ? 8 : 5));
  }
  commands.push("BT");
  commands.push("0.36 0.38 0.42 rg");
  commands.push("/F1 8 Tf");
  commands.push(`${MARGIN_X} 34 Td`);
  commands.push(`${encodePdfText(`HAUFFE OS Customer Value Workspace - ${language === "en" ? "Page" : "Seite"} ${pageIndex + 1}/${pageCount}`)} Tj`);
  commands.push("ET");
  return commands.join("\n");
}

function paginate(lines: PdfLine[]): PdfLine[][] {
  const pages: PdfLine[][] = [];
  let current: PdfLine[] = [];
  let pageIndex = 0;
  let y = pageStartY(pageIndex);
  for (const line of lines) {
    const needed = (line.gapBefore ?? 0) + Math.max(LINE_HEIGHT, line.size + (line.kind === "section" ? 8 : 5));
    if (current.length && y - needed < MARGIN_BOTTOM) {
      pages.push(current);
      current = [];
      pageIndex += 1;
      y = pageStartY(pageIndex);
    }
    current.push(line);
    y -= needed;
  }
  if (current.length) pages.push(current);
  return pages;
}

export function createAiSummaryPdfFilename(workspaceName: string): string {
  return `hauffe-os-auswertung-${slugifyFilename(workspaceName)}.pdf`;
}

type PdfObject = { body: string } | { dictionary: string; stream: Uint8Array };

function stringToPdfBytes(value: string): Uint8Array {
  const bytes = new Uint8Array(value.length);
  for (let index = 0; index < value.length; index += 1) bytes[index] = value.charCodeAt(index) & 0xff;
  return bytes;
}

function concatBytes(chunks: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const length = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const output = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.length;
  }
  return output;
}

function serializePdfObject(objectNumber: number, object: PdfObject): Uint8Array {
  if ("body" in object) {
    return stringToPdfBytes(`${objectNumber} 0 obj\n${object.body}\nendobj\n`);
  }
  return concatBytes([
    stringToPdfBytes(`${objectNumber} 0 obj\n${object.dictionary}\nstream\n`),
    object.stream,
    stringToPdfBytes("\nendstream\nendobj\n")
  ]);
}

export function createAiSummaryPdf(input: AiSummaryPdfInput, options: PdfBrandImages = {}): Uint8Array<ArrayBuffer> {
  const language = input.language ?? "de";
  const pages = paginate(buildReportLines(input));
  const objects: PdfObject[] = [];
  const firstImageObjectNumber = 3 + pages.length * 2;
  const logoObjectNumber = options.logo ? firstImageObjectNumber : undefined;
  const imageCount = options.logo ? 1 : 0;
  const fontRegularObjectNumber = 3 + pages.length * 2 + imageCount;
  const fontBoldObjectNumber = fontRegularObjectNumber + 1;

  objects.push({ body: "<< /Type /Catalog /Pages 2 0 R >>" });
  objects.push({ body: `<< /Type /Pages /Kids [${pages.map((_, index) => `${3 + index * 2} 0 R`).join(" ")}] /Count ${pages.length} >>` });

  pages.forEach((pageLines, index) => {
    const pageObjectNumber = 3 + index * 2;
    const contentObjectNumber = pageObjectNumber + 1;
    const content = buildPageContent(pageLines, index, pages.length, { logo: Boolean(options.logo) }, language);
    const xObjectEntries = [
      logoObjectNumber ? `/Logo ${logoObjectNumber} 0 R` : ""
    ]
      .filter(Boolean)
      .join(" ");
    const xObjectResource = xObjectEntries ? ` /XObject << ${xObjectEntries} >>` : "";
    objects.push({
      body: `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 ${fontRegularObjectNumber} 0 R /F2 ${fontBoldObjectNumber} 0 R >>${xObjectResource} >> /Contents ${contentObjectNumber} 0 R >>`
    });
    objects.push({ dictionary: `<< /Length ${stringToPdfBytes(content).length} >>`, stream: stringToPdfBytes(content) });
  });

  if (options.logo) {
    objects.push({
      dictionary: `<< /Type /XObject /Subtype /Image /Width ${options.logo.width} /Height ${options.logo.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${options.logo.bytes.length} >>`,
      stream: options.logo.bytes
    });
  }
  objects.push({ body: "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>" });
  objects.push({ body: "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>" });

  const chunks: Uint8Array[] = [stringToPdfBytes("%PDF-1.4\n")];
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(chunks.reduce((sum, chunk) => sum + chunk.length, 0));
    chunks.push(serializePdfObject(index + 1, object));
  });
  const xrefOffset = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  let xref = `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const offset of offsets.slice(1)) {
    xref += `${String(offset).padStart(10, "0")} 00000 n \n`;
  }
  xref += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  chunks.push(stringToPdfBytes(xref));
  return concatBytes(chunks);
}

async function loadImageForPdf(src: string, width: number, height: number, background: string): Promise<PdfLogoImage | undefined> {
  if (typeof Image === "undefined" || typeof document === "undefined") return undefined;
  const image = new Image();
  image.decoding = "async";
  image.src = src;
  await image.decode();
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return undefined;
  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
  const base64 = dataUrl.split(",")[1];
  if (!base64) return undefined;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return { bytes, width: canvas.width, height: canvas.height };
}

async function loadBrandImagesForPdf(): Promise<PdfBrandImages> {
  const logo = await loadImageForPdf(HAUFFE_LOGO.src, HAUFFE_LOGO.width, HAUFFE_LOGO.height, "#050505");
  return { logo };
}

export async function downloadAiSummaryPdf(input: AiSummaryPdfInput): Promise<void> {
  const brandImages = await loadBrandImagesForPdf();
  const pdf = createAiSummaryPdf(input, brandImages);
  const blob = new Blob([pdf.buffer.slice(pdf.byteOffset, pdf.byteOffset + pdf.byteLength)], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = createAiSummaryPdfFilename(input.workspaceName);
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
